import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  AppliedFilter,
  ChipsFilter,
  RangeFilter,
} from '@shared/components/filters/Domain/filter.model';
import { ChipModule } from 'primeng/chip';
import { Chips, ChipsModule } from 'primeng/chips';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { MessageModule } from 'primeng/message';
import { PRIMENGModule } from '@shared/prime-ng/prime-ng.module';
import { ChildProcessWithoutNullStreams } from 'node:child_process';

@Component({
  selector: 'app-chips-filter',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    ChipModule,
    ChipsModule,
    TooltipModule,
    TranslateModule,
    MessageModule,
    PRIMENGModule,
  ],
  templateUrl: './chips-filter.component.html',
  styleUrl: './chips-filter.component.scss',
})
export class ChipsFilterComponent implements OnInit, OnChanges {
  @Input() filter!: AppliedFilter;
  @Output() filterChange = new EventEmitter<AppliedFilter>();

  chipForm!: FormGroup;
  chipCount: number = 0;
  tooltip: string = '';
  chipFilter!: ChipsFilter;
  private typingTimer: any;
  private debounceDelay = 2000;

  private previousFilterValue: any;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.transformToChipsFilter();
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // To create multiple chip inputs with multiple bindings to the form
    if (changes['filter'] && !changes['filter'].firstChange) {
      // Only reinitialize if the filter value has changed
      if (JSON.stringify(this.previousFilterValue) !== JSON.stringify(this.filter.value)) {
        this.transformToChipsFilter();
        this.initializeForm();
        this.previousFilterValue = [...this.filter.value];
      }
    }
  }

  onChipInputKeyup(chipsComponent: Chips): void {
    //Add input value when keyUp for 2s
    const inputEl: HTMLInputElement = chipsComponent.inputViewChild?.nativeElement;
    if (!inputEl) return;

    clearTimeout(this.typingTimer);
    const value = inputEl.value.trim();

    if (!value) return;

    this.typingTimer = setTimeout(() => {
      this.addChipFromInput(chipsComponent);
    }, this.debounceDelay);
  }

  addChipFromInput(chipsComponent: Chips): void {
    const inputEl = chipsComponent.inputViewChild?.nativeElement;
    const inputValue = inputEl?.value?.trim();

    if (!inputValue) return;

    const chipsControl = this.chipForm.get('chips');
    const currentChips: string[] = chipsControl?.value || [];

    // Add if not duplicate
    if (!currentChips.includes(inputValue)) {
      chipsControl?.setValue([...currentChips, inputValue]);
      chipsControl?.markAsDirty();
      chipsControl?.markAsTouched();
      this.updateChipCount();
    }

    inputEl.value = '';
  }

  private transformToChipsFilter(): void {
    if (this.filter?.filter) {
      this.chipFilter = this.filter.filter as ChipsFilter;
      this.tooltip = this.chipFilter.tooltip ?? '';

      // Ensure value is always an array
      if (!Array.isArray(this.filter.value)) {
        this.filter.value = this.filter.value ? String(this.filter.value).split(',') : [];
      }
    } else {
      console.error('Invalid filter type, expected AppliedFilter<ChipsFilter>');
    }
  }

  private initializeForm(): void {
    if (this.chipForm) {
      this.chipForm.reset({ chips: this.filter.value });
    } else {
      this.chipForm = this.fb.group({
        chips: [this.filter.value],
      });
    }

    this.updateChipCount();
    this.chipForm.get('chips')?.valueChanges.subscribe((chips: string[]) => {
      if (!Array.isArray(chips)) return;

      const normalized = chips.map((c) => this.normalizeValue(c.trim()));

      if (JSON.stringify(normalized) !== JSON.stringify(chips)) {
        this.chipForm.get('chips')?.setValue(normalized, { emitEvent: false });
      }

      this.updateChipCount();
      this.emitUpdatedFilter();
    });
  }

  private updateChipCount(): void {
    const currentValue = this.chipForm.get('chips')?.value;
    this.chipCount = Array.isArray(currentValue) ? currentValue.length : 0;
  }

  private emitUpdatedFilter(): void {
    const chipsValue = this.chipForm.get('chips')?.value || [];

    const appliedFilter: AppliedFilter = {
      ...this.filter,
      isApplied: chipsValue.length > 0,
      value: chipsValue,
    };

    this.filterChange.emit(appliedFilter);
  }

  private normalizeValue(input: string): string {
    if (
      this.filter?.filter.id === 'phone' ||
      this.filter?.filter.id === 'contactPhone' ||
      this.filter?.filter.id === 'phones'
    ) {
      const clean = input.replace(/\s|-/g, '');
      if (/^\d+$/.test(clean)) {
        console.log('clean +', clean);
        return '+' + clean;
      }
      if (/^\+\d+$/.test(clean)) {
        console.log('clean', clean);
        return clean;
      }
    }
    return input;
  }
}
